import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Building2, ClipboardList, SlidersHorizontal, Plus, Search, Loader2, Trash2, ExternalLink, CheckCircle2, Pencil, KeyRound,
} from "lucide-react";
import api from "../../api/api";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
import { seatPrice, seatTotal } from "../../utils/orgPricing";
import { monthEnd, quote } from "../org/OrgLicenses";
import {
    Alert, EmptyState, Field, OrgModal, PageHeader, Panel, Pill, TabBar, apiError, formatDate, useOrgStyles, ORG_TYPE_LABEL,
} from "../../components/org/orgUi";

const TABS = [
    { key: "orgs", label: "Organizations", icon: Building2 },
    { key: "requests", label: "License requests", icon: KeyRound },
    { key: "assessments", label: "Assessments", icon: ClipboardList },
    { key: "evaluations", label: "Custom evaluations", icon: SlidersHorizontal },
];

const DEFAULTS = {
    education: { coachLicenseLimit: 25, programLimit: 25, studentLicenseLimit: 0 },
    enterprise: { coachLicenseLimit: 1, programLimit: 1, studentLicenseLimit: 0 },
};

/** Admin → Organizations: orgs (approve, limits, sponsored plans), scoped assessments, custom evaluations. */
export default function OrganizationManagement() {
    const [tab, setTab] = useState("orgs");
    const [options, setOptions] = useState({ tiers: [], surveys: [], programs: [] });
    const [orgs, setOrgs] = useState([]);

    const loadOptions = useCallback(async () => {
        try {
            const [opt, list] = await Promise.all([api.get("/org/admin/options"), api.get("/org/admin/orgs")]);
            setOptions(opt.data);
            setOrgs(list.data.organizations || []);
        } catch { /* each tab shows its own load error */ }
    }, []);

    useEffect(() => { loadOptions(); }, [loadOptions]);

    // Pending license requests show as a count on the tab, so they get noticed.
    const [pendingRequests, setPendingRequests] = useState(0);
    const loadPendingCount = useCallback(() => {
        api.get("/org/admin/license-requests", { params: { status: "pending" } })
            .then((res) => setPendingRequests((res.data.requests || []).length))
            .catch(() => setPendingRequests(0));
    }, []);
    useEffect(() => { loadPendingCount(); }, [loadPendingCount]);
    const tabs = TABS.map((t) => (t.key === "requests" && pendingRequests > 0 ? { ...t, label: `${t.label} (${pendingRequests})` } : t));

    return (
        <div className="mx-auto max-w-7xl px-0 md:px-4 py-2 md:py-6">
            <PageHeader title="Organizations" subtitle="Education and Enterprise accounts, their licenses, and the custom assessments and evaluations assigned to them." />
            <TabBar tabs={tabs} active={tab} onChange={setTab} label="Organization admin sections" />
            {tab === "orgs" && <OrgsTab tiers={options.tiers} onChanged={loadOptions} />}
            {tab === "requests" && <LicenseRequestsTab orgs={orgs} onChanged={() => { loadPendingCount(); loadOptions(); }} />}
            {tab === "assessments" && <AssessmentsTab options={options} orgs={orgs} />}
            {tab === "evaluations" && <EvaluationsTab options={options} orgs={orgs} />}
        </div>
    );
}

// ── Organizations ────────────────────────────────────────────────────────────

/** "used / seats" where seats = the limit set here + seats added through license requests (paid or free). */
function SeatCount({ used, base, purchased }) {
    const s = useOrgStyles();
    const total = base + (purchased || 0);
    return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
            {used} / {total}
            {total > 0 && used >= total && <Pill tone="danger">Full</Pill>}
            {purchased > 0 && <span className={`text-xs ${s.muted}`}>({purchased} added)</span>}
        </span>
    );
}

function OrgsTab({ tiers, onChanged }) {
    const s = useOrgStyles();
    const [orgs, setOrgs] = useState(null);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | {} | org

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("search", search.trim());
            if (status) params.set("status", status);
            const res = await api.get(`/org/admin/orgs?${params}`);
            setOrgs(res.data.organizations || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load organizations."));
            setOrgs([]);
        }
    }, [search, status]);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    const approve = async (org) => {
        try {
            await api.put(`/org/${org.OrgID}`, { status: "active" });
            await Promise.all([load(), onChanged()]);
        } catch (err) { setError(apiError(err)); }
    };

    const archive = async (org) => {
        if (!window.confirm(`Archive ${org.OrgName}? Its licenses end and it disappears from lists. This can't be undone from the UI.`)) return;
        try {
            await api.delete(`/org/admin/orgs/${org.OrgID}`);
            await Promise.all([load(), onChanged()]);
        } catch (err) { setError(apiError(err)); }
    };

    const pendingCount = (orgs || []).filter((o) => o.Status === "pending").length;

    return (
        <>
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Panel
                padded={false}
                title={pendingCount ? <span className="inline-flex items-center gap-2">Organizations <Pill tone="warning">{pendingCount} awaiting approval</Pill></span> : "Organizations"}
                actions={<button type="button" className={s.btnPrimary} onClick={() => setEditing({})}><Plus size={16} /> New organization</button>}
            >
                <div className={`flex flex-col gap-3 border-b px-5 py-4 md:flex-row ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                    <div className="relative flex-1">
                        <Search size={16} className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${s.muted}`} />
                        <input aria-label="Search organizations" placeholder="Search by name or contact email" className={`${s.input} pl-9`}
                               value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <select aria-label="Filter by status" className={`${s.input} md:w-48`} value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="">All statuses</option>
                        <option value="pending">Pending approval</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                    </select>
                </div>
                {!orgs ? (
                    <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                ) : orgs.length === 0 ? (
                    <EmptyState icon={Building2} title="No organizations found" />
                ) : (
                    <div className="relative overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                    <th className={s.th}>Organization</th>
                                    <th className={s.th}>Status</th>
                                    <th className={s.th}>Students</th>
                                    <th className={s.th}>Programs</th>
                                    <th className={s.th}>Coach seats</th>
                                    <th className={s.th}>Student seats</th>
                                    <th className={s.th}><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {orgs.map((o) => (
                                    <tr key={o.OrgID} className={`border-b last:border-0 ${s.row}`}>
                                        <td className={s.td}>
                                            <div className={`font-semibold ${s.text}`}>{o.OrgName}</div>
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                <Pill tone="primary">{ORG_TYPE_LABEL[o.OrgType]}</Pill>
                                                {o.IsSystem ? <Pill>System</Pill> : null}
                                            </div>
                                            {o.PrimaryContactEmail && <div className={`mt-1 text-xs ${s.muted}`}>{o.PrimaryContactName} · {o.PrimaryContactEmail}</div>}
                                        </td>
                                        <td className={s.td}>
                                            <Pill tone={o.Status === "active" ? "success" : o.Status === "pending" ? "warning" : "danger"}>{o.Status}</Pill>
                                            {o.Status === "pending" && (
                                                <div className={`mt-1 text-xs ${s.muted}`}>{o.ContactEmailVerified ? "Email verified" : "Email not verified"} · {formatDate(o.CreatedOn)}</div>
                                            )}
                                        </td>
                                        <td className={`${s.td} tabular-nums`}>{o.StudentCount}</td>
                                        <td className={`${s.td} tabular-nums`}>{o.ProgramCount} / {o.ProgramLimit}</td>
                                        <td className={`${s.td} tabular-nums`}><SeatCount used={o.CoachLicensesUsed} base={o.CoachLicenseLimit} purchased={o.CoachLicensesPurchased} /></td>
                                        <td className={`${s.td} tabular-nums`}><SeatCount used={o.StudentLicensesUsed} base={o.StudentLicenseLimit} purchased={o.StudentLicensesPurchased} /></td>
                                        <td className={`${s.td} text-right`}>
                                            <div className="flex justify-end gap-1">
                                                {o.Status === "pending" && (
                                                    <button type="button" className={s.btnSm} onClick={() => approve(o)}><CheckCircle2 size={14} /> Approve</button>
                                                )}
                                                <Link to={`/org?orgId=${o.OrgID}`} className={s.btnSm} title="Open the organization portal" aria-label={`Manage ${o.OrgName}`}>
                                                    <ExternalLink size={14} /><span className="hidden xl:inline">Manage</span>
                                                </Link>
                                                <button type="button" className={s.btnSm} onClick={() => setEditing(o)} aria-label={`Edit ${o.OrgName}`}><Pencil size={14} /></button>
                                                {!o.IsSystem && (
                                                    <button type="button" className={s.btnDanger} onClick={() => archive(o)} aria-label={`Archive ${o.OrgName}`}><Trash2 size={14} /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
            {editing && (
                <OrgEditor org={editing} tiers={tiers} onClose={() => setEditing(null)}
                           onSaved={async () => { setEditing(null); await Promise.all([load(), onChanged()]); }} />
            )}
        </>
    );
}

function OrgEditor({ org, tiers, onClose, onSaved }) {
    const s = useOrgStyles();
    const isNew = !org.OrgID;
    const [form, setForm] = useState({
        orgName: org.OrgName || "",
        orgType: org.OrgType || "education",
        status: org.Status || "active",
        primaryContactName: org.PrimaryContactName || "",
        primaryContactEmail: org.PrimaryContactEmail || "",
        primaryContactPhone: org.PrimaryContactPhone || "",
        coachLicenseLimit: org.CoachLicenseLimit ?? DEFAULTS.education.coachLicenseLimit,
        studentLicenseLimit: org.StudentLicenseLimit ?? DEFAULTS.education.studentLicenseLimit,
        programLimit: org.ProgramLimit ?? DEFAULTS.education.programLimit,
        sponsoredStudentTierId: org.SponsoredStudentTierID || "",
        sponsoredCoachTierId: org.SponsoredCoachTierID || "",
    });
    const [admin, setAdmin] = useState({ firstName: "", lastName: "", email: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onType = (e) => {
        const orgType = e.target.value;
        // New orgs follow the type defaults (Education 25/25, Enterprise 1/1).
        setForm((f) => (isNew ? { ...f, orgType, ...DEFAULTS[orgType] } : { ...f, orgType }));
    };

    const studentTiers = tiers.filter((t) => !t.Audiences?.length || t.Audiences.includes("Student") || t.Audiences.includes("Enterprise"));
    const coachTiers = tiers.filter((t) => !t.Audiences?.length || t.Audiences.includes("Coach") || t.Audiences.includes("Enterprise"));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        const payload = {
            ...form,
            sponsoredStudentTierId: form.sponsoredStudentTierId || null,
            sponsoredCoachTierId: form.sponsoredCoachTierId || null,
        };
        try {
            if (isNew) {
                const res = await api.post("/org/admin/orgs", { ...payload, orgAdmin: admin.email ? admin : undefined });
                if (res.data.orgAdminError) {
                    setError(`Organization created, but the org admin login wasn't: ${res.data.orgAdminError}`);
                    setSaving(false);
                    return;
                }
            } else {
                await api.put(`/org/${org.OrgID}`, payload);
            }
            await onSaved();
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    const addAdmin = async () => {
        setError("");
        setNotice("");
        try {
            await api.post(`/org/admin/orgs/${org.OrgID}/org-admins`, admin);
            setAdmin({ firstName: "", lastName: "", email: "" });
            setNotice("Org admin login created. They set their password with “Forgot password” on the login page.");
        } catch (err) {
            setError(apiError(err));
        }
    };

    return (
        <OrgModal
            isOpen
            wide
            onClose={onClose}
            title={isNew ? "New organization" : `Edit ${org.OrgName}`}
            footer={<>
                <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" form="org-editor" className={s.btnPrimary} disabled={saving}>
                    {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create organization" : "Save"}
                </button>
            </>}
        >
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
            <form id="org-editor" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
                <Field label="Organization name" htmlFor="oe-name" className="sm:col-span-2">
                    <input id="oe-name" className={s.input} value={form.orgName} onChange={set("orgName")} required maxLength={200} />
                </Field>
                <Field label="Type" htmlFor="oe-type" hint="Only Enterprise organizations can view student details.">
                    <select id="oe-type" className={s.input} value={form.orgType} onChange={onType} disabled={!!org.IsSystem}>
                        <option value="education">Education</option>
                        <option value="enterprise">Enterprise</option>
                    </select>
                </Field>
                {!isNew && (
                    <Field label="Status" htmlFor="oe-status" hint="Approving a pending organization activates its org admin login.">
                        <select id="oe-status" className={s.input} value={form.status} onChange={set("status")} disabled={!!org.IsSystem}>
                            <option value="pending">Pending approval</option>
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                        </select>
                    </Field>
                )}
                <Field label="Coach licenses" htmlFor="oe-coach">
                    <input id="oe-coach" type="number" min={0} className={s.input} value={form.coachLicenseLimit} onChange={set("coachLicenseLimit")} />
                </Field>
                <Field label="Student licenses" htmlFor="oe-student" hint="Per month.">
                    <input id="oe-student" type="number" min={0} className={s.input} value={form.studentLicenseLimit} onChange={set("studentLicenseLimit")} />
                </Field>
                <Field label="Programs" htmlFor="oe-programs">
                    <input id="oe-programs" type="number" min={0} className={s.input} value={form.programLimit} onChange={set("programLimit")} />
                </Field>
                {/* old <Field label="Plan for licensed students" htmlFor="oe-stier" hint="Applies only when it's better than the student's own plan."> */}
                {/* A license now stacks with the student's own plan instead of replacing it: paid plans'
                    monthly uses add up, a free plan only sets a floor, features from either plan unlock */}
                <Field label="Plan for licensed students" htmlFor="oe-stier" hint="Stacks with the student's own plan: monthly uses from paid plans add up (a free plan doesn't add), and features from either plan unlock.">
                    <select id="oe-stier" className={s.input} value={form.sponsoredStudentTierId} onChange={set("sponsoredStudentTierId")}>
                        <option value="">None</option>
                        {studentTiers.map((t) => <option key={t.TierID} value={t.TierID}>{t.TierName}</option>)}
                    </select>
                </Field>
                <Field label="Plan for licensed coaches" htmlFor="oe-ctier">
                    <select id="oe-ctier" className={s.input} value={form.sponsoredCoachTierId} onChange={set("sponsoredCoachTierId")}>
                        <option value="">None</option>
                        {coachTiers.map((t) => <option key={t.TierID} value={t.TierID}>{t.TierName}</option>)}
                    </select>
                </Field>
                <Field label="Primary contact" htmlFor="oe-contact">
                    <input id="oe-contact" className={s.input} value={form.primaryContactName} onChange={set("primaryContactName")} maxLength={200} />
                </Field>
                <Field label="Contact email" htmlFor="oe-cemail">
                    <input id="oe-cemail" type="email" className={s.input} value={form.primaryContactEmail} onChange={set("primaryContactEmail")} maxLength={255} />
                </Field>
                <Field label="Contact phone" htmlFor="oe-phone">
                    <input id="oe-phone" className={s.input} value={form.primaryContactPhone} onChange={set("primaryContactPhone")} maxLength={50} />
                </Field>
            </form>

            <div className={`mt-6 rounded-xl border p-4 ${s.subPanel}`}>
                <h4 className={`mb-1 text-sm font-bold ${s.text}`}>{isNew ? "Org admin login (optional)" : "Add an org admin login"}</h4>
                <p className={`mb-3 text-xs ${s.muted}`}>Creates a login for someone who manages the organization. They set a password with “Forgot password”. The email can't already have a Build My Biz Brand account.</p>
                <div className="grid gap-3 sm:grid-cols-3">
                    <input aria-label="Org admin first name" placeholder="First name" className={s.input} value={admin.firstName}
                           onChange={(e) => setAdmin((a) => ({ ...a, firstName: e.target.value }))} />
                    <input aria-label="Org admin last name" placeholder="Last name" className={s.input} value={admin.lastName}
                           onChange={(e) => setAdmin((a) => ({ ...a, lastName: e.target.value }))} />
                    <input aria-label="Org admin email" type="email" placeholder="Email" className={s.input} value={admin.email}
                           onChange={(e) => setAdmin((a) => ({ ...a, email: e.target.value }))} />
                </div>
                {!isNew && (
                    <button type="button" className={`${s.btnSecondary} mt-3`} onClick={addAdmin}
                            disabled={!admin.firstName || !admin.lastName || !admin.email}>
                        <Plus size={14} /> Add org admin
                    </button>
                )}
            </div>
        </OrgModal>
    );
}

// ── Scope picker (org or program) ────────────────────────────────────────────

// ── License requests ─────────────────────────────────────────────────────────

const REQUEST_FILTERS = [
    { key: "pending", label: "Waiting for approval" },
    { key: "approved", label: "Waiting for payment" },
    { key: "active", label: "Active" },
    { key: "", label: "All" },
];
const REQUEST_TONE = { pending: "warning", approved: "primary", active: "success", merged: "success", ended: "neutral", rejected: "danger", cancelled: "neutral" };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const perPeriod = (billingMode) => (billingMode === "yearly" ? "per year" : "per month");

/** License requests: set up licenses for an organization (only Build My Biz Brand
 *  sets counts; the org pays next, or gets the seats right away when the tier has
 *  no group price), approve or decline older requests, cancel unpaid ones, change
 *  a plan's seats or renewal, or end active ones. */
function LicenseRequestsTab({ orgs, onChanged }) {
    const s = useOrgStyles();
    const [status, setStatus] = useState("pending");
    const [requests, setRequests] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [settingUp, setSettingUp] = useState(false);
    const [seatsFor, setSeatsFor] = useState(null); // plan row whose seats are being changed

    const load = useCallback(async () => {
        try {
            const res = await api.get("/org/admin/license-requests", { params: status ? { status } : {} });
            setRequests(res.data.requests || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load license requests."));
            setRequests([]);
        }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const act = async (r, fn) => {
        setBusyId(r.RequestID);
        setError("");
        setNotice("");
        try {
            await fn();
            await load();
            onChanged();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusyId(null);
        }
    };

    const approve = (r) => {
        const priced = r.TotalAmount != null && Number(r.TotalAmount) > 0;
        const msg = priced
            ? `Approve ${r.Quantity} ${r.LicenseType} licenses for ${r.OrgName}?\n\nThey'll be asked to pay ${money(r.TotalAmount)} ${perPeriod(r.BillingMode)} (renews automatically); the licenses are added once they pay.`
            : `Approve ${r.Quantity} ${r.LicenseType} licenses for ${r.OrgName}?\n\nThere's no group price, so the licenses are added right away and stay until you end them.`;
        if (window.confirm(msg)) act(r, () => api.post(`/org/admin/license-requests/${r.RequestID}/review`, { approve: true }));
    };
    const decline = (r) => {
        const note = window.prompt(`Decline ${r.OrgName}'s request? Add a short reason they'll see (optional):`, "");
        if (note !== null) act(r, () => api.post(`/org/admin/license-requests/${r.RequestID}/review`, { approve: false, note }));
    };
    const end = (r) => {
        // "purchased" only for paid seats: seats set up for free were never bought
        const msg = `End ${r.OrgName}'s ${r.Quantity} ${r.TotalAmount > 0 ? "purchased " : ""}${r.LicenseType} licenses?\n\nThey keep them through the end of this month.${r.TotalAmount > 0 ? " The automatic payment stops now." : ""}`;
        if (window.confirm(msg)) act(r, () => api.post(`/org/admin/license-requests/${r.RequestID}/end`));
    };
    // A request that isn't paid yet (e.g. set up with the wrong number). While it's
    // open, a new set-up for the same license type is refused.
    const cancel = (r) => {
        if (window.confirm("Cancel this request? The organization won't be asked to pay for it.")) {
            act(r, () => api.delete(`/org/${r.OrgID}/license-requests/${r.RequestID}`));
        }
    };
    // A plan's automatic renewal (the org admin can't change it). Off keeps the
    // licenses through the month the paid period ends in.
    const setRenewal = (r, renew) => {
        const ends = r.CurrentPeriodEnd ? formatDate(monthEnd(r.CurrentPeriodEnd)) : "the end of the paid period";
        const ok = renew || window.confirm(
            `Turn renewal off for ${r.OrgName}'s ${r.Quantity} ${r.LicenseType} licenses?\n\nThey won't be charged again. They keep the licenses until ${ends}, then they end.`);
        if (!ok) return;
        act(r, async () => {
            await api.post(`/org/${r.OrgID}/license-requests/${r.RequestID}/auto-renew`, { renew });
            setNotice(renew ? `Renewal is back on for ${r.OrgName}.` : `Renewal is off for ${r.OrgName}. The licenses last until ${ends}.`);
        });
    };
    // A new request is approved or active right away; show it under its own filter
    const onSetUp = async (request, orgName) => {
        setSettingUp(false);
        const qty = `${request?.Quantity ?? ""} ${request?.LicenseType ?? ""} licenses`.trim();
        setNotice(request?.Status === "active"
            ? `${qty} for ${orgName} are active now. They stay until you end them.`
            : `${qty} are set up for ${orgName}. They'll see Pay now on their Licenses page.`);
        if (REQUEST_FILTERS.some((f) => f.key === request?.Status) && request.Status !== status) setStatus(request.Status);
        else await load();
        onChanged();
    };

    return (
        <Panel
            title="License requests"
            actions={<>
                <select aria-label="Filter by status" className={`${s.input} max-w-[14rem]`} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {REQUEST_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <button type="button" className={s.btnPrimary} onClick={() => { setNotice(""); setSettingUp(true); }}>
                    <Plus size={16} /> Set up licenses
                </button>
            </>}
            padded={false}
        >
            <div className="px-5 pt-4">
                <Alert onClose={() => setError("")}>{error}</Alert>
                <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
            </div>
            {requests === null ? (
                <InlineTableSkeleton isDarkMode={s.dark} rows={4} cols={5} bordered={false} />
            ) : requests.length === 0 ? (
                <EmptyState icon={KeyRound} title="No requests here"
                            body="Use Set up licenses to give an organization licenses; organizations can't ask for a number themselves. Prices come from the group pricing in Membership Management → Edit Tier." />
            ) : (
                <div className="relative overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                <th className={s.th}>Organization</th>
                                <th className={s.th}>Licenses</th>
                                <th className={s.th}>Price</th>
                                <th className={s.th}>Status</th>
                                <th className={s.th}><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r) => (
                                <tr key={r.RequestID} className={`border-b last:border-0 ${s.row}`}>
                                    <td className={s.td}>
                                        <div className={`font-semibold ${s.text}`}>{r.OrgName}</div>
                                        <div className={`text-xs ${s.muted}`}>{r.RequestedBy || "Org admin"} · {formatDate(r.CreatedOn)}</div>
                                        {r.Note && <div className={`mt-1 text-xs ${s.body}`}>“{r.Note}”</div>}
                                    </td>
                                    <td className={s.td}>
                                        <div className={s.text}>{r.Quantity} {r.LicenseType}</div>
                                        {r.TierName && <div className={`text-xs ${s.muted}`}>{r.TierName}</div>}
                                    </td>
                                    <td className={`${s.td} whitespace-nowrap tabular-nums`}>
                                        {r.TotalAmount != null ? (
                                            <>
                                                <div className={s.text}>{money(r.TotalAmount)} {perPeriod(r.BillingMode)}</div>
                                                <div className={`text-xs ${s.muted}`}>
                                                    {money(seatPrice(r.UnitPrice, r.PercentOff))} each
                                                    {Number(r.PercentOff) > 0 ? ` (${money(r.UnitPrice)} − ${Math.round(Number(r.PercentOff) * 100) / 100}%)` : ""}
                                                </div>
                                                {r.ScheduledQuantity != null && (
                                                    <div className={`text-xs ${s.muted}`}>
                                                        {r.Status === "active" ? `from next renewal (${r.ScheduledQuantity} licenses)` : `for ${r.ScheduledQuantity} licenses`}
                                                    </div>
                                                )}
                                            </>
                                        ) : <span className={s.muted}>No group price</span>}
                                    </td>
                                    <td className={s.td}>
                                        <Pill tone={REQUEST_TONE[r.Status]}>{r.Status === "merged" ? "added to plan" : r.Status}</Pill>
                                        {r.PaidOn && <div className={`mt-1 text-xs ${s.muted}`}>Paid {formatDate(r.PaidOn)}</div>}
                                        {r.Status === "active" && !!r.CancelAtPeriodEnd && <div className={`mt-1 text-xs ${s.muted}`}>Renewal off</div>}
                                    </td>
                                    <td className={`${s.td} text-right`}>
                                        <div className="flex justify-end gap-1">
                                            {r.Status === "pending" && <>
                                                <button type="button" className={s.btnSm} disabled={busyId === r.RequestID} onClick={() => approve(r)}><CheckCircle2 size={14} /> Approve</button>
                                                <button type="button" className={s.btnDanger} disabled={busyId === r.RequestID} onClick={() => decline(r)}>Decline</button>
                                            </>}
                                            {(r.Status === "pending" || r.Status === "approved") && (
                                                <button type="button" className={s.btnDanger} disabled={busyId === r.RequestID} onClick={() => cancel(r)}>Cancel</button>
                                            )}
                                            {r.Status === "active" && !!r.AutoRenews && !r.CancelAtPeriodEnd && (
                                                <button type="button" className={s.btnSm} disabled={busyId === r.RequestID} onClick={() => setSeatsFor(r)}>Change seats</button>
                                            )}
                                            {r.Status === "active" && !!r.AutoRenews && (
                                                <button type="button" className={s.btnSm} disabled={busyId === r.RequestID} onClick={() => setRenewal(r, !!r.CancelAtPeriodEnd)}>
                                                    {r.CancelAtPeriodEnd ? "Turn renewal on" : "Turn renewal off"}
                                                </button>
                                            )}
                                            {r.Status === "active" && (
                                                <button type="button" className={s.btnDanger} disabled={busyId === r.RequestID} onClick={() => end(r)}>End</button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {settingUp && <SetUpLicensesModal orgs={orgs} onClose={() => setSettingUp(false)} onDone={onSetUp} />}
            {seatsFor && (
                <PlanSeatsModal plan={seatsFor} onClose={() => setSeatsFor(null)}
                                onSaved={async (seats) => {
                                    const plan = seatsFor;
                                    setSeatsFor(null);
                                    setNotice(seats === plan.Quantity
                                        ? `${plan.OrgName}'s plan keeps its ${plan.Quantity} licenses.`
                                        : `From ${plan.CurrentPeriodEnd ? formatDate(plan.CurrentPeriodEnd) : "the next renewal"}, ${plan.OrgName}'s plan has ${seats} licenses.`);
                                    await load();
                                    onChanged();
                                }} />
            )}
        </Panel>
    );
}

/** Build My Biz Brand sets up licenses for an organization: the request is
 *  created already approved, priced from the org's group pricing (the org admin
 *  then pays on their Licenses page), or active right away when there's no price. */
function SetUpLicensesModal({ orgs, onClose, onDone }) {
    const s = useOrgStyles();
    const activeOrgs = orgs.filter((o) => o.Status === "active");
    const [form, setForm] = useState({ orgId: "", licenseType: "student", quantity: "", note: "" });
    const [orgData, setOrgData] = useState(null); // { pricing, requests } of the chosen org
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // The chosen org's group pricing and current requests, for the price shown below
    useEffect(() => {
        setOrgData(null);
        if (!form.orgId) return undefined;
        let cancelled = false;
        api.get(`/org/${form.orgId}/license-requests`)
            .then((res) => { if (!cancelled) setOrgData({ pricing: res.data.pricing || {}, requests: res.data.requests || [] }); })
            .catch(() => { if (!cancelled) setOrgData({ pricing: {}, requests: [] }); });
        return () => { cancelled = true; };
    }, [form.orgId]);

    const quantity = Math.max(0, Math.min(1000, parseInt(form.quantity, 10) || 0));
    const pricing = orgData?.pricing?.[form.licenseType] || null;
    const plan = (orgData?.requests || []).find((r) => r.LicenseType === form.licenseType && r.Status === "active" && r.AutoRenews);
    const openRequest = (orgData?.requests || []).find((r) => r.LicenseType === form.licenseType && (r.Status === "pending" || r.Status === "approved"));
    // Same math as the backend: with a plan, the discount step follows the plan's new total
    const planSeats = plan ? (plan.ScheduledQuantity ?? plan.Quantity) : 0;
    const q = pricing && quantity > 0 ? quote(pricing, planSeats + quantity) : null;
    const period = pricing?.BillingMode === "yearly" ? "per year" : "per month";

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const res = await api.post(`/org/admin/orgs/${form.orgId}/license-requests`, {
                licenseType: form.licenseType, quantity, note: form.note.trim() || undefined,
            });
            const orgName = activeOrgs.find((o) => String(o.OrgID) === String(form.orgId))?.OrgName || "the organization";
            await onDone(res.data.request, orgName);
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal
            isOpen
            onClose={onClose}
            title="Set up licenses"
            footer={<>
                <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" form="setup-licenses" className={s.btnPrimary} disabled={saving || !form.orgId || quantity < 1}>
                    {saving && <Loader2 size={14} className="animate-spin" />} Set up licenses
                </button>
            </>}
        >
            <Alert onClose={() => setError("")}>{error}</Alert>
            <form id="setup-licenses" onSubmit={submit} className="space-y-4">
                <Field label="Organization" htmlFor="sl-org">
                    <select id="sl-org" className={s.input} value={form.orgId} onChange={set("orgId")} required>
                        <option value="">Choose an organization…</option>
                        {activeOrgs.map((o) => <option key={o.OrgID} value={o.OrgID}>{o.OrgName}</option>)}
                    </select>
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="License type" htmlFor="sl-type">
                        <select id="sl-type" className={s.input} value={form.licenseType} onChange={set("licenseType")}>
                            <option value="student">Student</option>
                            <option value="coach">Coach</option>
                        </select>
                    </Field>
                    <Field label="How many" htmlFor="sl-qty">
                        <input id="sl-qty" type="number" min="1" max="1000" required className={s.input} value={form.quantity} onChange={set("quantity")} />
                    </Field>
                </div>
                {form.orgId && orgData && (
                    <div className={`rounded-xl border p-3 text-sm ${s.subPanel} ${s.body}`}>
                        {!pricing ? (
                            <p>There's no group price for {form.licenseType} licenses, so they're added right away and stay until you end them.</p>
                        ) : !q ? (
                            <p>{money(pricing.UnitPrice)} per license {period}, less volume discounts.</p>
                        ) : (
                            <p>
                                {plan ? <>Their plan goes from {planSeats} to <strong className={s.text}>{planSeats + quantity}</strong> licenses: these {quantity} add </> : <>They pay </>}
                                <strong className={s.text}>{money(seatTotal(q.each, quantity))}</strong> {period} ({money(q.each)} each
                                {q.pct > 0 ? `, ${Math.round(Number(q.pct) * 100) / 100}% off` : ""}).
                                {" "}The licenses are added once they pay on their Licenses page{plan ? " (charged for the rest of this billing period)" : ""}.
                            </p>
                        )}
                        {openRequest && (
                            <p className={`mt-2 ${s.dark ? "text-amber-300" : "text-amber-700"}`}>
                                This organization already has {openRequest.Status === "pending" ? "a pending" : "an approved, unpaid"} request
                                for {openRequest.Quantity} {form.licenseType} licenses. Approve, decline or cancel it first.
                            </p>
                        )}
                    </div>
                )}
                <Field label="Note (optional)" htmlFor="sl-note" hint="For Build My Biz Brand's records, e.g. what was agreed with the organization.">
                    <textarea id="sl-note" rows={2} maxLength={500} className={s.input} value={form.note} onChange={set("note")} />
                </Field>
            </form>
        </OrgModal>
    );
}

/** Fewer seats on an org's plan from its next renewal (the backend only lowers;
 *  adding goes through Set up licenses). */
function PlanSeatsModal({ plan, onClose, onSaved }) {
    const s = useOrgStyles();
    const [seats, setSeats] = useState(String(plan.ScheduledQuantity ?? plan.Quantity));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            await api.post(`/org/${plan.OrgID}/license-requests/${plan.RequestID}/seats`, { seats: Number(seats) });
            await onSaved(Number(seats));
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal
            isOpen
            onClose={onClose}
            title={`Change seats: ${plan.OrgName}`}
            footer={<>
                <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" form="plan-seats" className={s.btnPrimary} disabled={saving || !seats}>
                    {saving && <Loader2 size={14} className="animate-spin" />} Save
                </button>
            </>}
        >
            <Alert onClose={() => setError("")}>{error}</Alert>
            <form id="plan-seats" onSubmit={submit} className="space-y-4">
                <p className={`text-sm ${s.body}`}>
                    Their plan has <strong className={s.text}>{plan.Quantity} {plan.LicenseType} licenses</strong>
                    {plan.CurrentPeriodEnd ? <> and renews on <strong className={s.text}>{formatDate(plan.CurrentPeriodEnd)}</strong></> : null}.
                </p>
                <Field label="Licenses from the next renewal" htmlFor="ps-seats"
                       hint={`Fewer licenses start at the next renewal; they keep all ${plan.Quantity} until then and aren't refunded. To add licenses, use Set up licenses.`}>
                    <input id="ps-seats" type="number" min="1" max={plan.Quantity} required className={s.input}
                           value={seats} onChange={(e) => setSeats(e.target.value)} />
                </Field>
            </form>
        </OrgModal>
    );
}

function ScopePicker({ orgs, programs, value, onChange, idPrefix }) {
    const s = useOrgStyles();
    const encoded = value.orgId ? `org:${value.orgId}` : value.programId ? `program:${value.programId}` : "";
    const byOrg = useMemo(() => {
        const map = new Map();
        programs.forEach((p) => {
            if (!map.has(p.OrgID)) map.set(p.OrgID, { name: p.OrgName, programs: [] });
            map.get(p.OrgID).programs.push(p);
        });
        return map;
    }, [programs]);
    return (
        <select id={`${idPrefix}-scope`} className={s.input} value={encoded}
                onChange={(e) => {
                    const [kind, id] = e.target.value.split(":");
                    onChange(kind === "org" ? { orgId: Number(id), programId: null } : kind === "program" ? { orgId: null, programId: Number(id) } : { orgId: null, programId: null });
                }}>
            <option value="">Choose an organization or program…</option>
            <optgroup label="Whole organization">
                {orgs.map((o) => <option key={`o${o.OrgID}`} value={`org:${o.OrgID}`}>{o.OrgName}</option>)}
            </optgroup>
            {[...byOrg.entries()].map(([orgId, g]) => (
                <optgroup key={orgId} label={`${g.name}: programs`}>
                    {g.programs.map((p) => <option key={`p${p.ProgramID}`} value={`program:${p.ProgramID}`}>{p.ProgramName}</option>)}
                </optgroup>
            ))}
        </select>
    );
}

function scopeLabel(row) {
    return row.ProgramID ? `${row.OrgName} → ${row.ProgramName}` : `${row.OrgName} (whole organization)`;
}

// ── Assessments ──────────────────────────────────────────────────────────────

function AssessmentsTab({ options, orgs }) {
    const s = useOrgStyles();
    const [rows, setRows] = useState(null);
    const [surveyId, setSurveyId] = useState("");
    const [scope, setScope] = useState({ orgId: null, programId: null });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/org/admin/survey-assignments");
            setRows(res.data.assignments || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load assignments."));
            setRows([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const assign = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await api.post("/org/admin/survey-assignments", { surveyId: Number(surveyId), ...scope });
            setSurveyId("");
            await load();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Remove "${row.Title}" from ${scopeLabel(row)}? Responses already submitted are kept.`)) return;
        try {
            await api.delete(`/org/admin/survey-assignments/${row.SurveyAssignmentID}`);
            await load();
        } catch (err) { setError(apiError(err)); }
    };

    return (
        <div className="grid gap-6 lg:grid-cols-3">
            <Panel title="Assign an assessment or survey">
                <p className={`mb-4 text-sm ${s.muted}`}>
                    Build it in <Link to="/admin/surveys" className={s.btnLink}>Survey Management</Link>, then assign it here.
                    Assigned surveys appear under My Coaching → Programs for those students only and no longer pop up for everyone.
                </p>
                <Alert onClose={() => setError("")}>{error}</Alert>
                <form onSubmit={assign} className="space-y-4">
                    <Field label="Survey" htmlFor="as-survey">
                        <select id="as-survey" className={s.input} value={surveyId} onChange={(e) => setSurveyId(e.target.value)} required>
                            <option value="">Choose a survey…</option>
                            {options.surveys.map((sv) => <option key={sv.SurveyID} value={sv.SurveyID}>{sv.Title}{sv.IsActive ? "" : " (inactive)"}</option>)}
                        </select>
                    </Field>
                    <Field label="Assign to" htmlFor="as-scope">
                        <ScopePicker orgs={orgs} programs={options.programs} value={scope} onChange={setScope} idPrefix="as" />
                    </Field>
                    <button type="submit" className={`${s.btnPrimary} w-full`} disabled={busy || !surveyId || !(scope.orgId || scope.programId)}>
                        {busy && <Loader2 size={14} className="animate-spin" />} Assign
                    </button>
                </form>
            </Panel>
            <Panel title="Assignments" padded={false} className="lg:col-span-2">
                {!rows ? (
                    <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                ) : rows.length === 0 ? (
                    <EmptyState icon={ClipboardList} title="Nothing assigned yet" body="Every student can take the standard Build My Biz Brand assessments. Assign extra ones to an organization or program here." />
                ) : (
                    <ul className={`divide-y ${s.divider}`}>
                        {rows.map((r) => (
                            <li key={r.SurveyAssignmentID} className="flex items-center justify-between gap-3 px-5 py-3">
                                <div className="min-w-0">
                                    <div className={`font-semibold ${s.text}`}>{r.Title}</div>
                                    <div className={`text-xs ${s.muted}`}>{scopeLabel(r)}</div>
                                </div>
                                <button type="button" className={s.btnDanger} onClick={() => remove(r)} aria-label={`Remove ${r.Title}`}><Trash2 size={14} /></button>
                            </li>
                        ))}
                    </ul>
                )}
            </Panel>
        </div>
    );
}

// ── Custom evaluations ───────────────────────────────────────────────────────

function EvaluationsTab({ options, orgs }) {
    const s = useOrgStyles();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | {} | {EvaluationID}

    const load = useCallback(async () => {
        try {
            const res = await api.get("/org/admin/evaluations");
            setRows(res.data.evaluations || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load evaluations."));
            setRows([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (row) => {
        if (!window.confirm(`Archive "${row.Name}"? ${scopeLabel(row)} goes back to the default Build My Biz Brand ${row.EvaluationType} scoring.`)) return;
        try {
            await api.delete(`/org/admin/evaluations/${row.EvaluationID}`);
            await load();
        } catch (err) { setError(apiError(err)); }
    };

    return (
        <>
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Panel
                title="Custom resume & interview evaluations"
                padded={false}
                actions={<button type="button" className={s.btnPrimary} onClick={() => setEditing({})}><Plus size={16} /> New evaluation</button>}
            >
                <p className={`px-5 pt-4 text-sm ${s.muted}`}>
                    Students in the organization or program can choose these criteria when they review a resume or practice an interview.
                    A program's evaluation takes priority over its organization's.
                </p>
                {!rows ? (
                    <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                ) : rows.length === 0 ? (
                    <EmptyState icon={SlidersHorizontal} title="No custom evaluations" body="Everyone is scored with the default Build My Biz Brand categories." />
                ) : (
                    <div className="relative overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                    <th className={s.th}>Evaluation</th>
                                    <th className={s.th}>Type</th>
                                    <th className={s.th}>Applies to</th>
                                    <th className={s.th}>Categories</th>
                                    <th className={s.th}><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.EvaluationID} className={`border-b last:border-0 ${s.row}`}>
                                        <td className={s.td}>
                                            <div className={`font-semibold ${s.text}`}>{r.Name}</div>
                                            {r.Description && <div className={`line-clamp-1 text-xs ${s.muted}`}>{r.Description}</div>}
                                        </td>
                                        <td className={s.td}><Pill tone="primary">{r.EvaluationType === "resume" ? "Resume" : "Interview"}</Pill></td>
                                        <td className={s.td}>{scopeLabel(r)}</td>
                                        <td className={`${s.td} tabular-nums`}>{r.CategoryCount}</td>
                                        <td className={`${s.td} text-right`}>
                                            <div className="flex justify-end gap-1">
                                                <button type="button" className={s.btnSecondary} onClick={() => setEditing(r)} aria-label={`Edit ${r.Name}`}><Pencil size={14} /></button>
                                                <button type="button" className={s.btnDanger} onClick={() => remove(r)} aria-label={`Archive ${r.Name}`}><Trash2 size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
            {editing && (
                <EvaluationEditor evaluationId={editing.EvaluationID} orgs={orgs} programs={options.programs}
                                  onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
            )}
        </>
    );
}

const blankCategory = () => ({ name: "", weight: 0, description: "", rubric: "" });

function EvaluationEditor({ evaluationId, orgs, programs, onClose, onSaved }) {
    const s = useOrgStyles();
    const isNew = !evaluationId;
    const [form, setForm] = useState({ evaluationType: "resume", name: "", description: "", additionalInstructions: "" });
    const [scope, setScope] = useState({ orgId: null, programId: null });
    const [categories, setCategories] = useState([blankCategory()]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [openRubric, setOpenRubric] = useState(null);

    useEffect(() => {
        if (isNew) return;
        api.get(`/org/admin/evaluations/${evaluationId}`)
            .then((res) => {
                const ev = res.data.evaluation;
                setForm({ evaluationType: ev.EvaluationType, name: ev.Name || "", description: ev.Description || "", additionalInstructions: ev.AdditionalInstructions || "" });
                setScope({ orgId: ev.OrgID, programId: ev.ProgramID });
                setCategories(ev.categories.map((c) => ({ name: c.Name, weight: c.Weight, description: c.Description || "", rubric: c.Rubric || "" })));
            })
            .catch((err) => setError(apiError(err)))
            .finally(() => setLoading(false));
    }, [evaluationId, isNew]);

    const total = categories.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
    const setCat = (i, key, value) => setCategories((cs) => cs.map((c, j) => (j === i ? { ...c, [key]: value } : c)));

    const loadDefaults = async () => {
        try {
            const path = form.evaluationType === "resume" ? "/admin/resume-scoring/categories" : "/admin/interview-scoring/categories";
            const res = await api.get(path);
            const active = (res.data.categories || []).filter((c) => c.isActive !== false && c.isActive !== 0);
            setCategories(active.map((c) => ({ name: c.Name, weight: c.Weight, description: c.Description || "", rubric: c.Rubric || "" })));
        } catch (err) {
            setError(apiError(err, "Couldn't load the default categories."));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        const payload = { ...form, categories: categories.map((c) => ({ ...c, weight: Number(c.weight) || 0 })) };
        try {
            if (isNew) await api.post("/org/admin/evaluations", { ...payload, ...scope });
            else await api.put(`/org/admin/evaluations/${evaluationId}`, payload);
            await onSaved();
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal
            isOpen
            wide
            onClose={onClose}
            title={isNew ? "New custom evaluation" : "Edit evaluation"}
            footer={<>
                <span className={`mr-auto self-center text-sm font-semibold tabular-nums ${total === 100 ? "text-emerald-600" : (s.dark ? "text-amber-300" : "text-amber-600")}`}>
                    Weights: {total} / 100
                </span>
                <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" form="eval-editor" className={s.btnPrimary} disabled={saving || loading || total !== 100 || (isNew && !(scope.orgId || scope.programId))}>
                    {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create" : "Save"}
                </button>
            </>}
        >
            <Alert onClose={() => setError("")}>{error}</Alert>
            {loading ? <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div> : (
                <form id="eval-editor" onSubmit={submit} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Type" htmlFor="ev-type">
                            <select id="ev-type" className={s.input} value={form.evaluationType} disabled={!isNew}
                                    onChange={(e) => setForm((f) => ({ ...f, evaluationType: e.target.value }))}>
                                <option value="resume">Resume review</option>
                                <option value="interview">Interview prep</option>
                            </select>
                        </Field>
                        <Field label="Applies to" htmlFor="ev-scope" hint={isNew ? undefined : "To move an evaluation, archive it and create a new one."}>
                            {isNew
                                ? <ScopePicker orgs={orgs} programs={programs} value={scope} onChange={setScope} idPrefix="ev" />
                                : <input id="ev-scope" className={s.input} disabled
                                         value={scope.programId ? programs.find((p) => p.ProgramID === scope.programId)?.ProgramName || "Program" : orgs.find((o) => o.OrgID === scope.orgId)?.OrgName || "Organization"} />}
                        </Field>
                        <Field label="Name" htmlFor="ev-name" className="sm:col-span-2">
                            <input id="ev-name" className={s.input} required maxLength={200} value={form.name}
                                   onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. PVAMU Career Fair Resume Standard" />
                        </Field>
                        <Field label="Description (shown to students)" htmlFor="ev-desc" className="sm:col-span-2">
                            <input id="ev-desc" className={s.input} maxLength={1000} value={form.description}
                                   onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                        </Field>
                        <Field label="Additional instructions for the AI (optional)" htmlFor="ev-instr" className="sm:col-span-2"
                               hint="Added to the scoring prompt, e.g. “Prefer one-page resumes; the Education section should come before Experience.”">
                            <textarea id="ev-instr" rows={3} className={s.input} maxLength={2000} value={form.additionalInstructions}
                                      onChange={(e) => setForm((f) => ({ ...f, additionalInstructions: e.target.value }))} />
                        </Field>
                    </div>

                    <div>
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <h4 className={`text-sm font-bold ${s.text}`}>Scoring categories</h4>
                            <div className="flex gap-3">
                                <button type="button" className={s.btnLink} onClick={loadDefaults}>Start from the default categories</button>
                                <button type="button" className={s.btnLink} onClick={() => setCategories((cs) => [...cs, blankCategory()])} disabled={categories.length >= 12}>
                                    + Add category
                                </button>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {categories.map((c, i) => (
                                <div key={i} className={`rounded-xl border p-3 ${s.subPanel}`}>
                                    <div className="flex gap-2">
                                        <input aria-label={`Category ${i + 1} name`} placeholder="Category name" required maxLength={100}
                                               className={s.input} value={c.name} onChange={(e) => setCat(i, "name", e.target.value)} />
                                        <div className="relative w-28 flex-shrink-0">
                                            <input aria-label={`Category ${i + 1} weight`} type="number" min={0} max={100} required
                                                   className={`${s.input} pr-7`} value={c.weight} onChange={(e) => setCat(i, "weight", e.target.value)} />
                                            <span className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm ${s.muted}`}>%</span>
                                        </div>
                                        <button type="button" className={s.btnDanger} aria-label={`Remove category ${i + 1}`}
                                                onClick={() => setCategories((cs) => cs.filter((_, j) => j !== i))} disabled={categories.length === 1}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <input aria-label={`Category ${i + 1} description`} placeholder="What this category measures" maxLength={500}
                                           className={`${s.input} mt-2`} value={c.description} onChange={(e) => setCat(i, "description", e.target.value)} />
                                    <button type="button" className={`mt-2 ${s.btnLink}`} onClick={() => setOpenRubric(openRubric === i ? null : i)}>
                                        {openRubric === i ? "Hide rubric" : c.rubric ? "Edit rubric" : "Add rubric (score bands)"}
                                    </button>
                                    {openRubric === i && (
                                        <textarea aria-label={`Category ${i + 1} rubric`} rows={4} maxLength={8000} className={`${s.input} mt-2 font-mono text-xs`}
                                                  placeholder={"- 90-100: …\n- 70-89: …\n- 0-69: …"} value={c.rubric} onChange={(e) => setCat(i, "rubric", e.target.value)} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </form>
            )}
        </OrgModal>
    );
}
